import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminHeader from '../../components/navigation/AdminHeader';
import AdminNavigation from '../../components/navigation/AdminNavigation';
import ProtectedRoute from '../../components/navigation/ProtectedRoute';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import FleetContactsTable from './components/FleetContactsTable';
import FleetContactFormModal from './components/FleetContactFormModal';
import { supabase } from '../../lib/supabase';

const FleetContactsManagement = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [yards, setYards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [deletingContact, setDeletingContact] = useState(null);
  const [notification, setNotification] = useState(null);
  const [filterYardId, setFilterYardId] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [suppressions, setSuppressions] = useState({});

  useEffect(() => {
    const userEmail = localStorage.getItem('userEmail');
    if (userEmail) setUser({ email: userEmail });
    fetchYards();
    fetchSuppressions();
  }, []);

  useEffect(() => {
    fetchContacts();
  }, [filterYardId]);

  const fetchSuppressions = async () => {
    try {
      const { data } = await supabase
        ?.from('email_suppressions')
        ?.select('email, reason, bounce_type, first_seen_at')
        ?.eq('suppressed', true);
      const map = {};
      (data ?? [])?.forEach(row => {
        map[row.email.toLowerCase().trim()] = row;
      });
      setSuppressions(map);
    } catch {
      // non-fatal
    }
  };

  const fetchYards = async () => {
    try {
      const { data, error } = await supabase?.from('yards')?.select('id, name')?.order('name');
      if (!error && data) setYards(data);
    } catch (err) {
      console.error('Error fetching yards:', err);
    }
  };

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let query = supabase?.from('fleet_contacts')?.select('id, name, phone, email, role, contactfleetmgr, yard_id, email_bad, yards(name)')?.order('name');

      if (filterYardId) {
        const { data: joinRows } = await supabase?.from('fleet_contact_yards')?.select('contact_id')?.eq('yard_id', filterYardId);

        const managerIds = (joinRows || [])?.map(r => r?.contact_id);

        const { data: nonMgrData } = await supabase?.from('fleet_contacts')?.select('id, name, phone, email, role, contactfleetmgr, yard_id, email_bad, yards(name)')?.eq('yard_id', filterYardId)?.eq('contactfleetmgr', false)?.order('name');

        let mgrData = [];
        if (managerIds?.length > 0) {
          const { data } = await supabase?.from('fleet_contacts')?.select('id, name, phone, email, role, contactfleetmgr, yard_id, email_bad, yards(name)')?.in('id', managerIds)?.eq('contactfleetmgr', true)?.order('name');
          mgrData = data || [];
        }

        const combined = [...(nonMgrData || []), ...mgrData];
        const uniqueById = Array.from(new Map(combined.map(c => [c.id, c]))?.values());
        await enrichWithAssignedYards(uniqueById);
        return;
      }

      const { data, error: fetchErr } = await query;
      if (fetchErr) throw fetchErr;
      await enrichWithAssignedYards(data || []);
    } catch (err) {
      console.error('Error fetching contacts:', err);
      setError('Failed to load fleet contacts');
    } finally {
      setLoading(false);
    }
  }, [filterYardId]);

  const enrichWithAssignedYards = async (rawContacts) => {
    const managerIds = rawContacts?.filter(c => c?.contactfleetmgr)?.map(c => c?.id);

    let yardsByContact = {};
    if (managerIds?.length > 0) {
      const { data: joinData } = await supabase?.from('fleet_contact_yards')?.select('contact_id, yard_id, yards(id, name)')?.in('contact_id', managerIds);

      (joinData || [])?.forEach(row => {
        if (!yardsByContact?.[row?.contact_id]) yardsByContact[row.contact_id] = [];
        if (row?.yards) yardsByContact?.[row?.contact_id]?.push(row?.yards);
      });
    }

    const enriched = rawContacts?.map(c => ({
      ...c,
      yardName: c?.yards?.name || null,
      assignedYards: c?.contactfleetmgr ? (yardsByContact?.[c?.id] || []) : [],
      selectedYardIds: c?.contactfleetmgr
        ? (yardsByContact?.[c?.id] || [])?.map(y => y?.id)
        : []
    }));

    setContacts(enriched);
    setLoading(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('sessionExpiry');
    localStorage.removeItem('userEmail');
    navigate('/admin-authentication-portal');
  };

  const handleEdit = (contact) => {
    setEditingContact(contact);
    setShowForm(true);
  };

  const handleDelete = (contact) => {
    setDeletingContact(contact);
  };

  const handleConfirmDelete = async () => {
    if (!deletingContact) return;
    try {
      await supabase?.from('fleet_contact_yards')?.delete()?.eq('contact_id', deletingContact?.id);

      const { error } = await supabase?.from('fleet_contacts')?.delete()?.eq('id', deletingContact?.id);

      if (error) throw error;
      showNotif('Contact deleted successfully');
      setDeletingContact(null);
      fetchContacts();
    } catch (err) {
      console.error('Delete error:', err);
      showNotif('Failed to delete contact', true);
    }
  };

  const handleSave = () => {
    setShowForm(false);
    setEditingContact(null);
    showNotif(editingContact ? 'Contact updated successfully' : 'Contact added successfully');
    fetchContacts();
    fetchSuppressions();
  };

  const showNotif = (message, isError = false) => {
    setNotification({ message, isError });
    setTimeout(() => setNotification(null), 3000);
  };

  const filteredContacts = contacts?.filter(c => {
    if (!searchQuery) return true;
    const q = searchQuery?.toLowerCase();
    return (c?.name?.toLowerCase()?.includes(q) ||
    c?.phone?.includes(q) || c?.email?.toLowerCase()?.includes(q));
  });

  // Count bouncing contacts for banner (passed up to parent or used locally)
  const bouncingContacts = contacts?.filter(c => c?.email_bad === true || (c?.email && suppressions?.[c?.email?.toLowerCase()?.trim()]));

  return (
    <ProtectedRoute>
      <div className="min-h-screen bg-background">
        <AdminHeader user={user} onLogout={handleLogout} />
        <AdminNavigation />

        <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-6 mt-[7.5rem]">
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
                  <Icon name="Users" size={24} color="var(--color-primary)" strokeWidth={2} />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-foreground">Fleet Contacts</h1>
                  <p className="text-sm text-muted-foreground">
                    Manage yard contacts and fleet managers
                  </p>
                </div>
              </div>
              <Button
                variant="default"
                size="sm"
                iconName="UserPlus"
                iconPosition="left"
                onClick={() => { setEditingContact(null); setShowForm(true); }}
              >
                Add Contact
              </Button>
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg shadow-elevation-1">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 px-4 py-3 border-b border-border">
              <div className="relative flex-1 max-w-sm">
                <Icon
                  name="Search"
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  strokeWidth={2}
                />
                <input
                  type="text"
                  placeholder="Search contacts..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  className="w-full h-9 pl-9 pr-3 text-sm bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>
              <div className="flex items-center space-x-2">
                <label className="text-xs text-muted-foreground whitespace-nowrap">Filter by yard:</label>
                <select
                  value={filterYardId}
                  onChange={(e) => setFilterYardId(e?.target?.value)}
                  className="h-9 px-3 text-sm bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                >
                  <option value="">All yards</option>
                  {yards?.map(y => (
                    <option key={y?.id} value={y?.id}>{y?.name}</option>
                  ))}
                </select>
              </div>
              <span className="text-xs text-muted-foreground ml-auto">
                {filteredContacts?.length} contact{filteredContacts?.length !== 1 ? 's' : ''}
              </span>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="flex items-center space-x-3">
                  <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-sm text-muted-foreground">Loading contacts...</span>
                </div>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center py-16">
                <Icon name="AlertCircle" size={32} color="var(--color-destructive)" strokeWidth={2} />
                <p className="text-sm text-destructive mt-2">{error}</p>
                <Button variant="outline" size="sm" className="mt-4" onClick={fetchContacts}>
                  Retry
                </Button>
              </div>
            ) : (
              <FleetContactsTable
                contacts={filteredContacts}
                onEdit={handleEdit}
                onDelete={handleDelete}
                filterYardId={filterYardId}
                suppressions={suppressions}
              />
            )}
          </div>
        </main>

        {/* Notification toast */}
        {notification && (
          <div className={`fixed bottom-4 right-4 z-[300] flex items-center space-x-2 px-4 py-3 rounded-lg shadow-elevation-3 text-sm font-medium ${
            notification?.isError
              ? 'bg-destructive text-destructive-foreground'
              : 'bg-success text-success-foreground'
          }`}>
            <Icon
              name={notification?.isError ? 'AlertCircle' : 'CheckCircle'}
              size={16}
              strokeWidth={2}
            />
            <span>{notification?.message}</span>
          </div>
        )}

        {/* Delete confirmation modal */}
        {deletingContact && (
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[200] flex items-center justify-center p-4">
            <div className="bg-card border border-border rounded-lg shadow-elevation-3 w-full max-w-sm p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="flex items-center justify-center w-10 h-10 bg-destructive/10 rounded-lg">
                  <Icon name="Trash2" size={20} color="var(--color-destructive)" strokeWidth={2} />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-foreground">Delete Contact</h3>
                  <p className="text-xs text-muted-foreground">This action cannot be undone</p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mb-6">
                Are you sure you want to delete <strong className="text-foreground">{deletingContact?.name}</strong>?
              </p>
              <div className="flex items-center justify-end space-x-3">
                <Button variant="outline" size="sm" onClick={() => setDeletingContact(null)}>
                  Cancel
                </Button>
                <Button variant="destructive" size="sm" onClick={handleConfirmDelete} iconName="Trash2">
                  Delete
                </Button>
              </div>
            </div>
          </div>
        )}

        {showForm && (
          <FleetContactFormModal
            contact={editingContact}
            yards={yards}
            onClose={() => { setShowForm(false); setEditingContact(null); }}
            onSave={handleSave}
          />
        )}
      </div>
    </ProtectedRoute>
  );
};

export default FleetContactsManagement;
